import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import {
	Popover,
	PopoverBody
} from 'reactstrap';

import { IconButton } from '@techchange/components';
import { messages } from '../../i18n';

import { RESOURCE_TYPES } from '../../constants';

const propTypes = {

	/**
	 * The resource.
	 */
	resource: PropTypes.shape({

		/**
		 * The resource ID.
		 */
		id: PropTypes.string.isRequired,

		/**
		 * The resource type.
		 */
		type: PropTypes.string.isRequired,

		/**
		 * The resource title.
		 */
		title: PropTypes.string.isRequired,

	}).isRequired,

	/**
	 * The ID of the DOM node to use as the popover target.
	 */
	popoverTargetId: PropTypes.string.isRequired,

	/**
	 * The ID of the DOM node to use as the popover container.
	 * Necessary for keyboard accessibility of popover contents.
	 */
	popoverContainerId: PropTypes.string.isRequired,

	/**
	 * True, if the current user is an administrator for the scope (defaults to false).
	 */
	isAdmin: PropTypes.bool,

	/**
	 * Flag indicating if the resource is currently being edited.
	 */
	isEditing: PropTypes.bool.isRequired,

	/**
	 * Flag indicating if the toolbar is open.
	 */
	isOpen: PropTypes.bool,

	/**
	 * Function to handle updating the toolbar visibility state.
	 */
	setShowToolbar: PropTypes.func.isRequired,

	/**
	 * Function to handle editing the resource.
	 */
	onEdit: PropTypes.func,

	/**
	 * Function to handle editing the rule.
	 */
	onEditRule: PropTypes.func,

	/**
	 * Function to handle editing the settings.
	 */
	onEditSettings: PropTypes.func,

};

const defaultProps = {
	isAdmin: false,
	isOpen: false,
};

const ResourceToolbar = (props) => {

	const {
		resource,
		popoverTargetId,
		popoverContainerId,
		isAdmin,
		isEditing,
		isOpen,
		setShowToolbar,
		onEdit,
		onEditRule,
		onEditSettings,
	} = props;

	const intl = useIntl();

	/**
	 * Triggers/maintains tooltip state on keyboard focus of the popover target OR
	 * its descendants (includings elements inside the popover body).
	 */
	useEffect(() => {

		const popoverTargetContainer = document.getElementById(popoverContainerId);
		const popoverTarget = document.getElementById(popoverTargetId);

		// Show popover if target has focus
		const onTargetFocusIn = () => {
			setShowToolbar(true);
		};

		// Hide popover if focus is moved to element outside of the container
		const onContainerFocusOut = (event) => {

			const focusedElementInContainer =
				popoverTargetContainer &&
				popoverTargetContainer.contains(event.relatedTarget);

			if (!focusedElementInContainer) {
				setShowToolbar(false);
			}

		};

		if (popoverTarget) {
			popoverTarget.addEventListener('focusin', onTargetFocusIn);
		}

		if (popoverTargetContainer) {
			popoverTargetContainer.addEventListener('focusout', onContainerFocusOut);
		}

		return () => {

			if (popoverTarget) {
				popoverTarget.removeEventListener('focusin', onTargetFocusIn);
			}

			if (popoverTargetContainer) {
				popoverTargetContainer.removeEventListener('focusout', onContainerFocusOut);
			}

		};

	}, [popoverTargetId, popoverContainerId, setShowToolbar]);

	const handleToggle = () => {

		setShowToolbar((prev) => {

			if (isAdmin && !isEditing) {
				return !prev;
			}

			return prev;

		});

	};

	return (
		<Popover
			className="tc-resource-toolbar"
			innerClassName="tc-resource-toolbar-inner"
			placement={resource.type === RESOURCE_TYPES.SLIDE ? 'left' : 'top'}
			trigger="click hover"
			container={popoverContainerId}
			target={popoverTargetId}
			aria-labelledby={popoverTargetId}
			isOpen={isOpen}
			toggle={handleToggle}
			// Add a delay so fade-out doesn't flicker when mousing over popover content
			delay={{ hide: 200 }}>
			<PopoverBody className="d-flex align-items-center">
				{onEdit &&
					<IconButton
						className="tc-resource-toolbar-icon text-muted"
						icon="fas fa-pencil-alt"
						size="30px"
						aria-label={intl.formatMessage(messages.TC_COMPONENTS_RESOURCE_TOOLBAR_BUTTON_ARIA_LABEL_EDIT_CONTENT, {
							title: resource.title
						})}
						title={intl.formatMessage(messages.TC_COMPONENTS_RESOURCE_TOOLBAR_BUTTON_TITLE_EDIT_CONTENT)}
						onClick={onEdit} />
				}
				{onEditRule &&
					<IconButton
						className="tc-resource-toolbar-icon text-muted"
						icon="fas fa-book"
						size="30px"
						aria-label={intl.formatMessage(messages.TC_COMPONENTS_RESOURCE_TOOLBAR_BUTTON_ARIA_LABEL_EDIT_RULE, {
							title: resource.title
						})}
						title={intl.formatMessage(messages.TC_COMPONENTS_RESOURCE_TOOLBAR_BUTTON_TITLE_EDIT_RULE)}
						onClick={onEditRule} />
				}
				{onEditSettings &&
					<IconButton
						className="tc-resource-toolbar-icon text-muted"
						icon="fas fa-wrench"
						size="30px"
						aria-label={intl.formatMessage(messages.TC_COMPONENTS_RESOURCE_TOOLBAR_BUTTON_ARIA_LABEL_EDIT_SETTINGS, {
							title: resource.title
						})}
						title={intl.formatMessage(messages.TC_COMPONENTS_RESOURCE_TOOLBAR_BUTTON_TITLE_EDIT_SETTINGS)}
						onClick={onEditSettings} />
				}
			</PopoverBody>
		</Popover>
	);
};

ResourceToolbar.propTypes = propTypes;
ResourceToolbar.defaultProps = defaultProps;

export default ResourceToolbar;
